<template>
  <div class="crud-container">
    <div class="button-container">
      <button @click="openCreateDialog" class="create-btn">Create Product</button>
    </div>


    <!-- Data Grid -->
    <table class="crud-table" v-if="items.length">
      <thead>
        <tr>
          <th>Product Id</th>
          <th>Name</th>
          <th>Amount</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in items" :key="item._id">
          <td>{{ item._id }}</td>
          <td>{{ item.name }}</td>
          <td>{{ item.amount }}</td>
          <td>
            <button @click="openEditDialog(item)" class="edit-btn">Edit</button>
            <button @click="deleteItem(item._id)" class="delete-btn">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination Controls -->
    <div class="pagination">
      <button :disabled="page === 1" @click="changePage(page - 1)">Previous</button>
      <span>Page {{ page }} of {{ totalPages }}</span>
      <button :disabled="page === totalPages" @click="changePage(page + 1)">Next</button>
    </div>

    <!-- Create/Edit Dialog -->
    <div v-if="dialog" class="dialog">
      <div class="dialog-content">
        <h3>{{ isEdit ? 'Edit Item' : 'Create Item' }}</h3><br />

        <!-- Main Item Fields -->
        <label>
          Name
          <input v-model="itemForm.name" />
        </label>
        <label>
          Amount
          <input v-model="itemForm.amount" />
        </label>

        <!-- Sub-items Section -->
        <div v-for="(subItem, index) in itemForm.items" :key="index" class="sub-item">
          <h4>Item {{ index + 1 }}</h4>

          <!-- Sub-item Name -->
          <label>
            <input v-model="subItem.name" />
          </label>

          <!-- Remove Button for Sub-item -->
          <button @click="removeSubItem(index)" class="remove-btn">Remove Item</button>
        </div>

        <!-- Add New Sub-item Button -->
        <button @click="addSubItem" class="add-btn">Add Item</button>

        <div class="dialog-actions">
          <button @click="dialog = false" class="cancel-btn">Cancel</button>
          <button @click="saveItem" class="save-btn">{{ isEdit ? 'Save' : 'Create' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue';
import instance from '@/axios'

export default defineComponent({
  setup() {
    const items = ref<any[]>([]);
    const dialog = ref(false);
    const isEdit = ref(false);
    const itemForm = ref({
      _id: 0,
      name: '',
      amount: '',
      items: [] as { name: '' }[]
    });

    // Pagination properties
    const page = ref(1);
    const totalItems = ref(0);
    const pageSize = ref(5);
    const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));


    const fetchItems = async () => {
      // Make API call with pagination parameters
      const response = await instance.get('/products/' + page.value);
      items.value = response.data.Result.data;
      totalItems.value = response.data.Result.totalCount; // Assuming the total item count is returned by the API
    };

    const openCreateDialog = () => {
      isEdit.value = false;
      itemForm.value = { _id: 0, name: '', amount: '', items: [] };
      dialog.value = true;
    };

    const openEditDialog = (item: any) => {
      isEdit.value = true;
      itemForm.value = { ...item };
      if (itemForm.value.items == null || itemForm.value.items.length < 1) {
        itemForm.value.items.push({ name: '' })
      }
      dialog.value = true;
    };

    // Add a new sub-item
    const addSubItem = () => {
      itemForm.value.items.push();
      itemForm.value.items.push({ name: '' })
    };

    // Remove a sub-item by its index
    const removeSubItem = (index: number) => {
      itemForm.value.items.splice(index, 1);
      if (itemForm.value.items == null || itemForm.value.items.length < 1) {
        itemForm.value.items.push({ name: '' })
      }
    };

    const saveItem = async () => {
      if (isEdit.value) {
        // Update item in items
        const index = items.value.findIndex((item) => item._id === itemForm.value._id);
        if (index !== -1) {
          items.value[index] = itemForm.value;
          await instance.put('/product', itemForm.value)
          fetchItems();
        }
      } else {
        // Create new item
        itemForm.value._id = items.value.length + 1; // Simple auto-increment for IDs
        items.value.push(itemForm.value);
        await instance.post('/product', itemForm.value)
        fetchItems();
      }
      dialog.value = false;
    };

    const deleteItem = async (_id: number) => {
      items.value = items.value.filter((item) => item._id !== _id);
      await instance.delete('/product/' + _id)
      fetchItems();
    };

    const changePage = (newPage: number) => {
      if (newPage < 1 || newPage > totalPages.value) return;
      page.value = newPage;
      fetchItems(); // Fetch data for the new page
    };

    onMounted(() => {
      fetchItems();
    });

    return {
      items,
      dialog,
      isEdit,
      itemForm,
      page,
      totalItems,
      totalPages,
      pageSize,
      openCreateDialog,
      openEditDialog,
      saveItem,
      changePage,
      deleteItem,
      removeSubItem,
      addSubItem,
    };
  },
});
  </script>
  
  <style scoped>

  /* General Reset */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body,
  html {
    height: 100%;
    font-family: Arial, sans-serif;
  }

  body {
    display: flex;
    justify-content: center;
    /* Horizontally center content */
    align-items: center;
    /* Vertically center content */
    min-height: 100%;
    flex-direction: column;
  }

  .sub-item {
    margin-bottom: 15px;
  }

  .add-btn,
  .remove-btn {
    margin-top: 10px;
  }

  /* Container for the CRUD UI */
  .crud-container {
    width: 100%;
    padding: 4px;
    box-sizing: border-box;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    /* Center align elements inside the container */

  }

  /* Container to hold buttons in a row */
  .button-container {
    display: flex;
    gap: 10px;
    /* Space between buttons */
    align-items: center;
    /* Vertically align the buttons if they are different heights */
  }

  /* Button styles */
  .create-btn {
    padding: 10px 20px;
    background-color: #4CAF50;
    color: white;
    border: none;
    cursor: pointer;
    font-size: 16px;
    border-radius: 4px;
    transition: background-color 0.3s ease;
  }

  /* Hover effect */
  .create-btn:hover {
    background-color: #45a049;
  }


  /* Table for displaying items */
  .crud-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    table-layout: auto;
    /* Auto-resize the columns based on content */
  }

  .crud-table th,
  .crud-table td {
    padding: 10px;
    /* Increased padding for better spacing */
    text-align: left;
    border: 1px solid #ddd;
    word-wrap: break-word;
    /* Allow words to wrap in cells */
    max-width: 500px;
    /* Limit column width to accommodate more content */
  }

  .crud-table th {
    background-color: #f4f4f4;
  }

  .crud-table td {
    word-break: break-word;
    /* Break long words and prevent overflow */
  }


  /* Action buttons in table rows */
  .edit-btn,
  .delete-btn {
    padding: 5px 10px;
    margin: 0 5px;
    cursor: pointer;
  }

  .edit-btn {
    background-color: #ffa500;
    color: white;
    border: none;
  }

  .delete-btn {
    background-color: #f44336;
    color: white;
    border: none;
  }

  .edit-btn:hover {
    background-color: #e68a00;
  }

  .delete-btn:hover {
    background-color: #e03131;
  }


  /* Pagination controls */
  .pagination {
    display: flex;
    justify-content: center;
    /* Horizontally center the pagination */
    align-items: center;
    margin-top: 20px;
  }

  .pagination button {
    padding: 5px 15px;
    margin: 0 10px;
    cursor: pointer;
  }

  .pagination button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* Dialog Styling */
  .dialog {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .dialog-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
    box-sizing: border-box;
    /* Make the dialog content scrollable */
    max-height: 85%;
    /* Define the max height for the dialog box */
    overflow-y: auto;
    /* Enable vertical scrolling */
  }

  .dialog-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
  }

  .dialog input {
    width: 100%;
    padding: 8px;
    margin-bottom: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  /* Action buttons inside dialog */
  .cancel-btn,
  .save-btn {
    padding: 8px 15px;
    background-color: #2196f3;
    color: white;
    border: none;
    cursor: pointer;
  }

  .cancel-btn:hover {
    background-color: #1e88e5;
  }

  .save-btn {
    background-color: #4caf50;
  }

  .save-btn:hover {
    background-color: #45a049;
  }

</style>
  
